import React from 'react';
import { cn } from '../../lib/cn';
import { Icon } from '../Icon/Icon';
import type { MetricState } from '../../dashboard/types';

/**
 * Shared furniture for the Dashboard (spec 17 §4, §10).
 *
 * Cards are the same shape as the settings cards but with no accent bar and no
 * row-save anything — this page has no forms and writes nothing.
 */

export function DashCard({
  title,
  sub,
  rightNow,
  headRight,
  children,
  className,
}: {
  title: string;
  sub?: React.ReactNode;
  /** **Do not omit it** on an always-now panel (§3.1): *"3 people are still
   * waiting"* means the same thing on a day view and a month view, and scoping it
   * would make it false. */
  rightNow?: boolean;
  headRight?: React.ReactNode;
  children: React.ReactNode;
  className?: string;
}) {
  return (
    <section
      className={cn(
        'overflow-hidden rounded-[18px] border border-[#E7EAF2] bg-[var(--surface-card)]',
        className,
      )}
      style={{ boxShadow: '0 1px 3px rgba(36,41,54,0.05)' }}
    >
      <header className="flex items-start justify-between gap-4 px-6 pb-3.5 pt-[19px]">
        <div className="min-w-0">
          <div className="flex items-center gap-2.5">
            <h2 className="m-0 text-[14.5px] font-bold tracking-[-0.01em] text-[var(--text-primary)]">{title}</h2>
            {rightNow === true ? <RightNowChip /> : null}
          </div>
          {sub ? (
            <p className="m-0 mt-1 max-w-[620px] text-[12px] leading-[1.6] text-[var(--text-muted)]">{sub}</p>
          ) : null}
        </div>
        {headRight ? <div className="shrink-0 text-right">{headRight}</div> : null}
      </header>
      {children}
    </section>
  );
}

/** Accent-soft background, accent text, uppercase, 10px (§3.1). */
export function RightNowChip() {
  return (
    <span className="shrink-0 rounded-full bg-[var(--brand-indigo-tint)] px-2 py-[3px] text-[10px] font-bold uppercase tracking-[0.06em] text-[var(--brand-indigo)]">
      Right now
    </span>
  );
}

/** A card footer strip. The footers are not decoration — ship the words (§5.1). */
export function DashFooterStrip({
  tone = 'plain',
  children,
}: {
  tone?: 'plain' | 'red' | 'indigo';
  children: React.ReactNode;
}) {
  const tones = {
    plain: { bg: '#FAFBFE', fg: 'var(--text-muted)', border: '#E7EAF2' },
    red: { bg: '#FCF0EF', fg: 'var(--alert-red)', border: '#F3D9D7' },
    indigo: { bg: 'var(--brand-indigo-tint)', fg: 'var(--text-secondary)', border: '#DDE1FA' },
  } as const;
  const t = tones[tone];
  return (
    <div
      className="px-6 py-[13px] text-[11.5px] leading-[1.6]"
      style={{ background: t.bg, color: t.fg, borderTop: `1px solid ${t.border}` }}
    >
      {children}
    </div>
  );
}

/** Every number opens the rows behind it (P329). Hover `#FAFBFE`. */
export function DashRowLink({
  href,
  children,
  className,
}: {
  href: string;
  children: React.ReactNode;
  className?: string;
}) {
  return (
    <a href={href} className={cn('block hover:bg-[#FAFBFE]', className)}>
      {children}
    </a>
  );
}

export function money(cents: number): string {
  return `$${Math.round(cents / 100).toLocaleString('en-US')}`;
}

export function count(n: number): string {
  return n.toLocaleString('en-US');
}

/**
 * The one place the empty-state rule is enforced (§10.1).
 *
 * **`not_set_up` and `unknown` never render a digit.** A 0 meaning *we do not
 * know* is indistinguishable from a 0 meaning *nothing happened*, and the two
 * lead a practice to opposite conclusions about whether ELVA is working.
 *
 * `goodZero` is the other half: *Nobody had to step in* is the best news on the
 * page and is green, not a sad grey nought (P346).
 */
export function Metric({
  value,
  format = 'count',
  size = 34,
  tone,
  goodZero,
  zeroNote,
  className,
}: {
  value: MetricState;
  format?: 'count' | 'money';
  size?: number;
  /** Explicit colour for a real figure — money green, alert red. */
  tone?: string;
  /** When the zero is an achievement rather than an absence. */
  goodZero?: boolean;
  /** The sentence under a zero, e.g. *Nothing came in during this period*. */
  zeroNote?: string;
  className?: string | undefined;
}) {
  const style = { fontSize: size, letterSpacing: size >= 30 ? '-0.035em' : '-0.01em' };

  if (value.state === 'not_set_up') {
    return (
      <span className={cn('inline-block', className)}>
        <span className="block font-bold text-[var(--text-muted)]" style={style}>
          —
        </span>
        <a href={value.setupHref} className="text-[11.5px] font-semibold text-[var(--brand-indigo)] hover:underline">
          Not set up yet →
        </a>
      </span>
    );
  }

  if (value.state === 'unknown') {
    return (
      <span className={cn('inline-block', className)}>
        <span className="block font-bold text-[var(--text-muted)]" style={style}>
          —
        </span>
        <span className="text-[11.5px] text-[var(--text-muted)]">last good read at {value.lastGoodRead}</span>
      </span>
    );
  }

  if (value.state === 'zero') {
    // Production booked is the exception the spec calls out: `$0`, not a dash —
    // nothing booked is worth nothing.
    const text = format === 'money' ? '$0' : '0';
    return (
      <span className={cn('inline-block', className)}>
        <span
          className="block font-bold"
          style={{ ...style, color: goodZero === true ? 'var(--money-green)' : 'var(--text-primary)' }}
        >
          {text}
        </span>
        {zeroNote !== undefined ? (
          <span
            className={cn(
              'text-[12.5px]',
              goodZero === true ? 'font-semibold text-[var(--money-green)]' : 'text-[var(--text-secondary)]',
            )}
          >
            {zeroNote}
          </span>
        ) : null}
      </span>
    );
  }

  const text = format === 'money' ? money(value.value) : count(value.value);
  return (
    <span className={cn('inline-block', className)}>
      <span className="block font-bold" style={{ ...style, color: tone ?? 'var(--text-primary)' }}>
        {text}
      </span>
      {value.state === 'too_few' ? (
        <span className="text-[11.5px] text-[var(--text-muted)]">too few to rate</span>
      ) : null}
    </span>
  );
}

/** A small state chip — the agent table's `Working` / `Blocked` / `Off`. */
export function StateChip({ label, tone }: { label: string; tone: 'green' | 'red' | 'grey' }) {
  const tones = {
    green: { bg: '#EEF6F2', fg: 'var(--money-green)' },
    red: { bg: '#FBEDEC', fg: 'var(--alert-red)' },
    grey: { bg: '#F1F3F9', fg: 'var(--text-muted)' },
  } as const;
  const t = tones[tone];
  return (
    <span
      className="inline-flex items-center gap-1.5 rounded-full px-2.5 py-1 text-[11px] font-bold"
      style={{ background: t.bg, color: t.fg }}
    >
      {tone === 'green' ? (
        <span className="h-[6px] w-[6px] rounded-full" style={{ background: t.fg }} aria-hidden />
      ) : null}
      {label}
    </span>
  );
}

/** The ring on a schedule day. `closed` and `Not set up` are not 0%. */
export function PercentRing({
  percent,
  label,
  colour,
  size = 52,
}: {
  percent: number | undefined;
  /** `closed`, or the percent — the caller decides, because 0% and closed differ. */
  label: string;
  colour: string;
  size?: number;
}) {
  const r = (size - 6) / 2;
  const c = 2 * Math.PI * r;
  const filled = percent === undefined ? 0 : Math.max(0, Math.min(100, percent));
  return (
    <span className="relative inline-flex shrink-0 items-center justify-center" style={{ width: size, height: size }}>
      <svg width={size} height={size} viewBox={`0 0 ${String(size)} ${String(size)}`} aria-hidden>
        <circle cx={size / 2} cy={size / 2} r={r} fill="none" stroke="#EDEFF5" strokeWidth={5} />
        {percent === undefined ? null : (
          <circle
            cx={size / 2}
            cy={size / 2}
            r={r}
            fill="none"
            stroke={colour}
            strokeWidth={5}
            strokeLinecap="round"
            strokeDasharray={`${String((filled / 100) * c)} ${String(c)}`}
            transform={`rotate(-90 ${String(size / 2)} ${String(size / 2)})`}
          />
        )}
      </svg>
      <span
        className="absolute font-[var(--font-mono)] text-[12.5px] font-bold"
        style={{ color: colour }}
      >
        {label}
      </span>
    </span>
  );
}

/** Ring colour: `≥90%` green · `≥70%` accent · below that amber (§5.4). */
export function ringColour(percent: number): string {
  if (percent >= 90) return 'var(--money-green)';
  if (percent >= 70) return 'var(--brand-indigo)';
  return 'var(--warn-amber)';
}

/** A muted link with the standard arrow. */
export function DashLink({ href, children }: { href: string; children: React.ReactNode }) {
  return (
    <a
      href={href}
      className="inline-flex items-center gap-1 text-[13px] font-semibold text-[var(--brand-indigo)] hover:underline"
    >
      {children}
      <Icon kind="right" size={12} stroke={2.2} />
    </a>
  );
}
